import { useEffect, useMemo, useState } from "react";
import {
  PieChart,
  Pie,
  Cell,
  ResponsiveContainer,
  Tooltip,
  Legend,
} from "recharts";
import { getProductBreakdown } from "services/finance.service";

const CATEGORY_COLORS = {
  "BV1 $497": "#0088FE",
  "Inner Circle $99/m": "#00C49F",
  "Blueprint $17": "#FFBB28",
  "Mentorship $5k": "#FF8042",
  "Mentorship Plus $15k": "#F43F5E",
  "Coach Mentorship": "#7C3AED",
  "Elite Circle": "#0EA5E9",
  Unmapped: "#94A3B8",
};

const CANONICAL_CATEGORIES = [
  "BV1 $497",
  "Inner Circle $99/m",
  "Blueprint $17",
  "Mentorship $5k",
  "Mentorship Plus $15k",
  "Coach Mentorship",
  "Elite Circle",
];

const formatCurrency = (value) =>
  `$${Number(value || 0).toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;

const isCustomRangeIncomplete = (filters) =>
  filters?.dateRangeType === "custom" &&
  (!filters?.customDateRange?.[0] || !filters?.customDateRange?.[1]);

const PieTooltip = ({ active, payload }) => {
  if (!active || !payload?.length) return null;
  const item = payload[0];
  return (
    <div className="bg-white p-3 border border-gray-200 shadow-sm rounded-md text-sm">
      <p className="font-medium text-gray-900">{item.name}</p>
      <p className="text-gray-600">{formatCurrency(item.value)}</p>
    </div>
  );
};

export default function ProductBreakdownSection({ api, filters }) {
  const [data, setData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 640);
    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    if (isCustomRangeIncomplete(filters)) {
      setIsLoading(false);
      setData(null);
      setError(null);
      return;
    }

    let cancelled = false;

    const load = async () => {
      setIsLoading(true);
      setError(null);
      try {
        const result = await getProductBreakdown(api, { filters });
        if (!cancelled) setData(result);
      } catch {
        if (!cancelled) {
          setData(null);
          setError("Failed to load product breakdown.");
        }
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [filters.dateRangeType, filters.customDateRange?.[0], filters.customDateRange?.[1]]);

  const products = useMemo(() => data?.products ?? [], [data?.products]);
  const pieData = useMemo(() => data?.pieChartData ?? [], [data?.pieChartData]);
  const totalRevenue = data?.totalRevenue ?? 0;
  const totalUnits = data?.totalUnits ?? 0;
  const activeCategories = useMemo(
    () => products.filter((item) => item.revenue > 0 || (item.unitsSold ?? item.qty ?? 0) > 0),
    [products]
  );
  const topCategory = useMemo(() => {
    if (!activeCategories.length) return null;
    return [...activeCategories].sort((a, b) => b.revenue - a.revenue)[0];
  }, [activeCategories]);

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden mb-6">
      <div className="px-4 py-3 border-b border-gray-200 bg-gray-50">
        <h3 className="text-sm font-medium text-gray-900">Product Breakdown</h3>
        <p className="text-xs text-gray-500 mt-0.5">
          Revenue split across $497 / $99 / $17 / $5k
        </p>
      </div>

      <div className="p-4">
        {error && (
          <div className="mb-4 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
            {error}
          </div>
        )}

        {isLoading ? (
          <div className="animate-pulse space-y-4">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <div className="h-64 bg-gray-200 rounded" />
              <div className="h-64 bg-gray-200 rounded" />
            </div>
          </div>
        ) : isCustomRangeIncomplete(filters) ? (
          <p className="text-sm text-gray-500 italic text-center py-10">
            Select start and end dates to view product breakdown.
          </p>
        ) : totalRevenue <= 0 && totalUnits <= 0 ? (
          <p className="text-sm text-gray-500 italic text-center py-10">
            No product sales for the selected period.
          </p>
        ) : (
          <>
            {products.some((item) => item.productName === "Unmapped") && (
              <div className="mb-4 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
                Some sales could not be mapped to a product category. Review Stripe product
                IDs or deploy updated product mapping.
              </div>
            )}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-6">
              <div className="rounded-lg border border-gray-200 p-3">
                <p className="text-xs text-gray-500">Total Revenue</p>
                <p className="text-lg font-semibold text-gray-900">{formatCurrency(totalRevenue)}</p>
              </div>
              <div className="rounded-lg border border-gray-200 p-3">
                <p className="text-xs text-gray-500">Units Sold</p>
                <p className="text-lg font-semibold text-gray-900">{totalUnits.toLocaleString()}</p>
              </div>
              <div className="rounded-lg border border-gray-200 p-3">
                <p className="text-xs text-gray-500">Categories</p>
                <p className="text-lg font-semibold text-gray-900">
                  {activeCategories.filter((item) => CANONICAL_CATEGORIES.includes(item.productName))
                    .length}
                </p>
              </div>
              <div className="rounded-lg border border-gray-200 p-3">
                <p className="text-xs text-gray-500">Top Category</p>
                <p className="text-sm font-semibold text-gray-900 truncate">
                  {topCategory?.productName || "-"}
                </p>
              </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <div>
                <h4 className="text-sm font-medium text-gray-800 mb-3">Product Distribution</h4>
                <div style={{ height: isMobile ? 260 : 300 }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={pieData}
                        dataKey="amount"
                        nameKey="name"
                        cx="50%"
                        cy="50%"
                        innerRadius={60}
                        outerRadius={isMobile ? 70 : 90}
                        paddingAngle={2}
                      >
                        {pieData.map((entry) => (
                          <Cell
                            key={entry.name}
                            fill={CATEGORY_COLORS[entry.name] ?? "#AF19FF"}
                          />
                        ))}
                      </Pie>
                      <Tooltip content={<PieTooltip />} />
                      <Legend />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
              </div>

              <div>
                <h4 className="text-sm font-medium text-gray-800 mb-3">Product Revenue</h4>
                <div className="overflow-x-auto">
                  <table className="min-w-full border border-gray-200 rounded-lg">
                    <thead className="bg-gray-50 text-gray-700">
                      <tr>
                        <th className="px-3 py-2 text-left text-xs font-semibold">Category</th>
                        <th className="px-3 py-2 text-right text-xs font-semibold">Units</th>
                        <th className="px-3 py-2 text-right text-xs font-semibold">Revenue</th>
                        <th className="px-3 py-2 text-right text-xs font-semibold">Share</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {products.map((item) => {
                        const share =
                          totalRevenue > 0
                            ? ((item.revenue / totalRevenue) * 100).toFixed(1)
                            : "0.0";
                        return (
                          <tr key={item.productName} className="hover:bg-gray-50">
                            <td className="px-3 py-2 text-sm text-gray-800">{item.productName}</td>
                            <td className="px-3 py-2 text-sm text-right text-gray-700">
                              {item.unitsSold ?? item.qty ?? 0}
                            </td>
                            <td className="px-3 py-2 text-sm text-right font-medium text-green-700">
                              {formatCurrency(item.revenue)}
                            </td>
                            <td className="px-3 py-2 text-sm text-right text-gray-600">{share}%</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
